/**
 * Das Aktionsmenü eines Artikels (KONZEPT.md §8.2, ADR 0013 §2).
 *
 * Was ein Artikel-QR öffnet. **Ganz oben die Aktionen, darunter die Details**
 * — wer scannt, will buchen, nicht lesen.
 *
 * Zwei getrennte Dinge (ADR 0005): den **Status** ändern und den Artikel
 * **verschieben**. Wohin, sagt die eine Zielauswahl „Wohin?“ -- ein Lagerort,
 * ein Packstück oder eine Einsatzstelle. Nach jeder Buchung steht
 * „Rückgängig“ in der Rückmeldung.
 */

import { useMemo, useState } from "react";
import { Link, useParams } from "react-router";
import type { Ziel } from "@thw/core-domain";
import { useOnline } from "../api/online.js";
import { buchungFuerHistorie } from "../lokal/beschreiben.js";
import {
  bestandLaden,
  useBestand,
  useLokal,
  verlaufLaden,
  type Bestand,
} from "../lokal/bestand.js";
import { konfliktFinden } from "../lokal/konflikt.js";
import { packstueckStandort } from "../lokal/packstueck.js";
import { stammplatzSetzen, stammplatzVon } from "../lokal/stammplatz.js";
import {
  ortDesArtikels,
  statusSetzen,
  verschieben,
  zielName,
  type Gebucht,
} from "../lokal/verschieben.js";
import { rueckmeldungGeben } from "../scan/Scanner.jsx";
import { Knopf, Ladeanzeige, Leer, Nummer, StatusPunkt, VerbleibSchild } from "../ui/bausteine.jsx";
import { nichtAmStammplatz, StammplatzZeile } from "../ui/Stammplatz.jsx";
import { useBuchungsmeldung } from "../ui/Buchungsmeldung.jsx";
import { EtikettHinweis } from "../ui/EtikettHinweis.jsx";
import { StatusWahl } from "../ui/StatusWahl.jsx";
import { Wohin } from "../ui/Wohin.jsx";

export function ArtikelSeite() {
  const { id = "" } = useParams();
  const [zeigeWohin, setZeigeWohin] = useState<"aus" | "verschieben" | "stammplatz">("aus");
  const online = useOnline();
  const [zeigeStatus, setZeigeStatus] = useState(false);
  const { setMeldung, anzeige } = useBuchungsmeldung();

  // Alles aus der lokalen Kopie (KONZEPT.md §15.1). Der Zustand enthaelt
  // schon, was noch in der Warteschlange liegt -- ohne das waere die Anzeige
  // nach jeder Buchung fuer einen Augenblick falsch, und im Funkloch dauerhaft.
  const bestand = useBestand();
  const verlauf = useLokal(() => verlaufLaden(id), [id]);

  /**
   * Eine Buchung absetzen.
   *
   * Die Rueckmeldung haengt am **Ergebnis**, nicht am Klick. Eine Meldung
   * "gebucht", die auch dann erscheint, wenn nichts gespeichert wurde, ist
   * schlimmer als gar keine: Man verlaesst sich darauf.
   */
  const ausfuehren = async (
    tun: (b: Bestand) => Promise<Gebucht>,
    text: (b: Bestand) => string,
    /** Danach den Stammplatz setzen -- der Haken beim Verschieben, nur mit Netz. */
    stammplatzDanach?: () => Promise<unknown>,
  ): Promise<void> => {
    try {
      // Frisch gelesen: Der Zustand „vorher“ fuer Rueckgaengig muss stimmen.
      const frisch = await bestandLaden();
      const gebucht = await tun(frisch);
      // null: kein Haken; "": gesetzt; sonst der Grund, warum nicht.
      let stammplatz: string | null = null;
      if (stammplatzDanach !== undefined) {
        try {
          await stammplatzDanach();
          stammplatz = "";
        } catch (fehler) {
          stammplatz = fehler instanceof Error ? fehler.message : String(fehler);
        }
      }
      rueckmeldungGeben(true);
      setMeldung({
        art: stammplatz === null || stammplatz === "" ? "gut" : "warnung",
        kopf: text(frisch),
        gebucht,
        ...(stammplatz === null
          ? {}
          : {
              zusatz:
                stammplatz === "" ? (
                  <EtikettHinweis bestand={frisch} artikel_ids={[id]} />
                ) : (
                  `Stammplatz nicht gesetzt: ${stammplatz}`
                ),
            }),
      });
    } catch (fehler) {
      // Nicht verschlucken: Wer nicht weiss, dass eine Buchung liegen blieb,
      // stellt das Geraet in den Schrank und glaubt, alles sei erfasst.
      rueckmeldungGeben(false);
      setMeldung({
        art: "warnung",
        kopf: `Nicht gebucht: ${fehler instanceof Error ? fehler.message : String(fehler)}`,
      });
    }
    setZeigeStatus(false);
  };

  const artikel = bestand?.artikel.get(id);

  /**
   * Die Historie, neueste zuerst -- mit dem, was noch wartet, und mit den
   * Buchungen, die beim Eintreffen schon von einer neueren ueberstimmt waren.
   * Die ueberstimmte bleibt sichtbar (KONZEPT.md §15.4), sie hat nur nie
   * gegolten. Was spaeter ganz gewoehnlich abgeloest wurde, ist nicht
   * ueberstimmt -- es war eine Weile richtig.
   */
  const historie = useMemo(() => {
    if (verlauf === undefined || artikel === undefined) return [];
    const alle = [...verlauf.log, ...verlauf.wartend];
    const kern = { id: artikel.id, art: artikel.art };
    const wartend = new Set(verlauf.wartend.map((b) => b.id));
    return alle
      .map((b) => ({
        buchung: b,
        wartet: wartend.has(b.id),
        ueberstimmt: konfliktFinden(kern, b, alle, null) !== null,
      }))
      .reverse();
  }, [verlauf, artikel]);

  if (bestand === undefined || verlauf === undefined) return <Ladeanzeige was="Der Artikel" />;
  if (artikel === undefined) {
    return (
      <Leer>
        Diesen Artikel gibt es im Bestand dieses Geräts nicht. Vielleicht ist er gerade erst
        angelegt worden — mit Netz kommt er beim nächsten Abgleich.
      </Leer>
    );
  }

  const zustand = bestand.zustaende.get(id);
  if (zustand === undefined) return <Ladeanzeige was="Der Artikel" />;
  const ort =
    zustand.lagerort_id === null ? null : (bestand.lagerorte.get(zustand.lagerort_id) ?? null);
  const einsatzstelle =
    zustand.einsatzstelle_id === null
      ? null
      : (bestand.einsatzstellen.get(zustand.einsatzstelle_id) ?? null);
  const eigenerOrt = ortDesArtikels(bestand, id);
  const statusFarbe =
    bestand.statusArten.find((a) => a.schluessel === zustand.status)?.farbe ?? "#a3a3a3";
  const stammplatz = stammplatzVon(bestand, id);
  // „Zurück an Stammplatz“ nur, wenn es einen gibt, er noch taugt und der
  // Artikel nicht dort ist (ADR 0013 §2).
  const zurueck =
    stammplatz !== null &&
    bestand.lagerorte.get(stammplatz)?.aktiv === true &&
    nichtAmStammplatz(bestand, id);

  /** Stammplatz setzen oder entfernen -- ein Stammdatum, nur mit Netz. */
  const stammplatzAendern = async (neu: string | null): Promise<void> => {
    try {
      await stammplatzSetzen([id], neu);
      rueckmeldungGeben(true);
      setMeldung({
        art: "gut",
        kopf:
          neu === null
            ? "Stammplatz entfernt"
            : `Stammplatz jetzt ${zielName(bestand, { art: "lagerort", id: neu })}`,
        zusatz: <EtikettHinweis bestand={bestand} artikel_ids={[id]} />,
      });
    } catch (fehler) {
      rueckmeldungGeben(false);
      setMeldung({
        art: "warnung",
        kopf: "Stammplatz nicht geändert",
        zusatz: fehler instanceof Error ? fehler.message : String(fehler),
      });
    }
  };

  if (zeigeWohin === "verschieben") {
    return (
      <Wohin
        titel={`Wohin? — ${artikel.bezeichnung}`}
        stammplatzAnbieten
        artikel_id={id}
        aufAbbruch={() => {
          setZeigeWohin("aus");
        }}
        aufWahl={(ziel: Ziel, alsStammplatz: boolean) => {
          setZeigeWohin("aus");
          void ausfuehren(
            (b) => verschieben(b, id, ziel),
            (b) => `✓ nach ${zielName(b, ziel)} verschoben`,
            alsStammplatz && ziel.art === "lagerort"
              ? () => stammplatzSetzen([id], ziel.id)
              : undefined,
          );
        }}
      />
    );
  }
  if (zeigeWohin === "stammplatz") {
    return (
      <Wohin
        titel={`Stammplatz — ${artikel.bezeichnung}`}
        artikel_id={id}
        zweck="stammplatz"
        aufAbbruch={() => {
          setZeigeWohin("aus");
        }}
        aufWahl={(ziel: Ziel) => {
          setZeigeWohin("aus");
          void stammplatzAendern(ziel.id);
        }}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <header className="rounded-xl bg-white p-4 dark:bg-neutral-800">
        <h1 className="text-2xl font-bold">{artikel.bezeichnung}</h1>
        <p className="mt-1">
          <Nummer>{artikel.inventarnummer}</Nummer>
          {zustand.bestand !== null && (
            <span className="ml-3 text-neutral-600 dark:text-neutral-300">
              Bestand {String(zustand.bestand)}
            </span>
          )}
        </p>
        {/* Ein Packstueck ist Artikel und Behaelter (KONZEPT.md §5.2). */}
        {eigenerOrt !== null && (
          <Link
            to={`/lagerort/${eigenerOrt.id}`}
            className="mt-2 flex min-h-12 items-center font-semibold text-blue-700 underline dark:text-blue-300"
          >
            Inhalt von {eigenerOrt.kennung} ansehen
          </Link>
        )}
      </header>

      {anzeige}

      {/*
        Status und Ort sind zwei getrennte Dinge (ADR 0005) -- und stehen
        deshalb in zwei Karten mit je einem eigenen Knopf. Ein defektes Geraet
        in die Werkstatt zu bringen aendert den Ort, nicht den Status.
      */}
      {/*
        Farbige Streifen trennen die drei Karten (Rückmeldungen 2.1): Status in
        seiner Farbe, Ort blau, Stammplatz grün. Breit genug für Sonne und Nacht.
      */}
      <section
        aria-label="Status"
        className="rounded-xl border-l-8 bg-white p-4 dark:bg-neutral-800"
        style={{ borderLeftColor: statusFarbe }}
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-neutral-500">Status</h2>
            <p className="mt-1 text-lg">
              <StatusPunkt status={zustand.status} />
            </p>
          </div>
          <Knopf
            onClick={() => {
              setZeigeStatus(!zeigeStatus);
            }}
          >
            Status ändern
          </Knopf>
        </div>

        {zeigeStatus && (
          <StatusWahl
            aktuell={zustand.status}
            aufWahl={(schluessel, bezeichnung) => {
              void ausfuehren(
                (b) => statusSetzen(b, id, schluessel),
                () => `✓ Status: ${bezeichnung}`,
              );
            }}
          />
        )}
      </section>

      <section
        aria-label="Ort"
        className="rounded-xl border-l-8 border-blue-600 bg-white p-4 dark:border-blue-400 dark:bg-neutral-800"
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-neutral-500">Ort</h2>
            <p className="mt-1 flex flex-wrap items-center gap-2 text-lg">
              <span className="font-semibold">
                {ort !== null
                  ? ort.standardziel
                    ? ort.bezeichnung
                    : `${ort.kennung} — ${ort.bezeichnung}`
                  : einsatzstelle !== null
                    ? einsatzstelle.bezeichnung
                    : zustand.verbleib === "im_lager"
                      ? "Lagerort unbekannt"
                      : "—"}
              </span>
              <VerbleibSchild verbleib={zustand.verbleib} />
            </p>
            {/* Im Packstueck: wo das Packstueck gerade ist (ADR 0012). */}
            {ort !== null && packstueckStandort(bestand, ort.id) !== null && (
              <p className="text-sm text-neutral-600 dark:text-neutral-300">
                Packstück {packstueckStandort(bestand, ort.id)}
              </p>
            )}
          </div>
          <Knopf
            art="haupt"
            onClick={() => {
              setZeigeWohin("verschieben");
              setZeigeStatus(false);
            }}
          >
            Verschieben
          </Knopf>
        </div>
      </section>

      {/*
        Wo er hingehört (ADR 0013 §6) -- getrennt vom Ort, wo er ist. Zurück
        geht es mit einem Tipp, auch offline: Das ist eine gewöhnliche Buchung.
      */}
      <section
        aria-label="Stammplatz"
        className="rounded-xl border-l-8 border-emerald-600 bg-white p-4 dark:border-emerald-400 dark:bg-neutral-800"
      >
        <StammplatzZeile
          bestand={bestand}
          artikel_id={id}
          online={online}
          aufAendern={() => {
            setZeigeWohin("stammplatz");
            setZeigeStatus(false);
          }}
          aufEntfernen={() => void stammplatzAendern(null)}
        />
        {zurueck && (
          <div className="mt-3 flex flex-col">
            <Knopf
              art="haupt"
              onClick={() => {
                const ziel: Ziel = { art: "lagerort", id: stammplatz };
                void ausfuehren(
                  (b) => verschieben(b, id, ziel),
                  (b) => `✓ zurück an ${zielName(b, ziel)}`,
                );
              }}
            >
              Zurück an Stammplatz
            </Knopf>
          </div>
        )}
      </section>

      <section aria-label="Details" className="rounded-xl bg-white p-4 dark:bg-neutral-800">
        <h2 className="mb-2 text-lg font-bold">Details</h2>
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
          {artikel.hersteller !== null && (
            <>
              <dt className="text-neutral-500">Hersteller</dt>
              <dd>{artikel.hersteller}</dd>
            </>
          )}
          {artikel.baujahr !== null && (
            <>
              <dt className="text-neutral-500">Baujahr</dt>
              <dd>{artikel.baujahr}</dd>
            </>
          )}
          {artikel.kennzeichen !== null && (
            <>
              <dt className="text-neutral-500">Kennzeichen</dt>
              <dd>{artikel.kennzeichen}</dd>
            </>
          )}
          {artikel.sachnummer !== null && (
            <>
              <dt className="text-neutral-500">Sachnummer</dt>
              <dd>{artikel.sachnummer}</dd>
            </>
          )}
          {artikel.einheit_zustaendig !== null && (
            <>
              <dt className="text-neutral-500">Einheit</dt>
              <dd>{artikel.einheit_zustaendig}</dd>
            </>
          )}
        </dl>

        {artikel.pruefungen.length > 0 && (
          <>
            <h3 className="mt-4 mb-1 font-bold">Prüfungen</h3>
            <ul className="text-sm">
              {artikel.pruefungen.map((p) => (
                <li key={p.art} className="text-neutral-600 dark:text-neutral-300">
                  {p.art}
                  {p.faellig !== null && ` — fällig ${p.faellig}`}
                </li>
              ))}
            </ul>
          </>
        )}
      </section>

      <section aria-label="Historie" className="rounded-xl bg-white p-4 dark:bg-neutral-800">
        <h2 className="mb-2 text-lg font-bold">Historie</h2>
        {historie.length === 0 ? (
          <Leer>Noch keine Buchung. Dieser Artikel wurde nie gescannt.</Leer>
        ) : (
          <ol className="flex flex-col gap-2 text-sm">
            {historie.map(({ buchung: b, wartet, ueberstimmt }) => (
              <li
                key={b.id}
                className={`text-neutral-600 dark:text-neutral-300 ${ueberstimmt ? "opacity-60" : ""}`}
              >
                <span className={ueberstimmt ? "line-through" : ""}>
                  {new Date(b.erfasst_am).toLocaleString("de-DE")} ·{" "}
                  {/* „verschoben nach GB-07", „mit GB-01 verschoben nach LKW-1" */}
                  {buchungFuerHistorie(
                    b,
                    bestand,
                    b.abgeleitet_von === null
                      ? undefined
                      : verlauf.ausloeserinnen.get(b.abgeleitet_von),
                  )}
                </span>
                <br />
                <span className="text-xs">
                  {bestand.benutzer.get(b.benutzer_id)?.anzeigename ?? "unbekannt"}
                  {b.modul !== null && ` · ${b.modul}`}
                  {b.kommentar !== null && ` · ${b.kommentar}`}
                  {wartet && (
                    <span className="ml-2 font-semibold text-amber-700 dark:text-amber-300">
                      wartet auf Abgleich
                    </span>
                  )}
                  {ueberstimmt && (
                    <span className="ml-2 font-semibold">von einer neueren Buchung überstimmt</span>
                  )}
                </span>
              </li>
            ))}
          </ol>
        )}
      </section>

      <Link
        to={`/etiketten?artikel=${artikel.id}`}
        className="flex min-h-12 items-center justify-center text-sm text-blue-700 underline dark:text-blue-300"
      >
        Etikett drucken (DataMatrix mit der Inventarnummer)
      </Link>
    </div>
  );
}
